import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import useAuth from './auth';
import { useConfigContext } from '../config/configContext';
import { useUserContext } from './userContext';
import { loadFromLocalStorage } from './localStorageHelper';
import { currentTenantLocalStorageKey } from '../support/constants';
import { Api } from '../../types/api';

const defaultAxiosInstance = axios.create({ baseURL: 'not-loaded' });

export const apiContext = createContext<Api.ApiContext>({
	apiInstance: defaultAxiosInstance
});

export const useApiContext = (): Api.ApiContext => useContext(apiContext);

const ApiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { config } = useConfigContext();
	const userContext = useUserContext();
	const { initialized, isAuthenticated, getAccessToken } = useAuth();
	const [isReady, setIsReady] = useState(false);
	const interceptorsConfigured = useRef(false);

	const [apiInstance] = useState(() =>
		axios.create({
			baseURL: `${config.baseUrl}/api/`,
		})
	);

	// Store getAccessToken in a ref to avoid stale closures
	const getAccessTokenRef = useRef(getAccessToken);
	getAccessTokenRef.current = getAccessToken;

	const userContextRef = useRef(userContext);
	userContextRef.current = userContext;

	useEffect(() => {
		if (initialized && isAuthenticated && !interceptorsConfigured.current) {
			// Configure interceptors only once
			const configureInterceptor = (instance: AxiosInstance) => {
				return instance.interceptors.request.use(
					async (requestConfig: AxiosRequestConfig) => {
						if (!requestConfig.headers) {
							requestConfig.headers = {};
						}

						// Get fresh access token using ref to avoid stale closure
						const token = await getAccessTokenRef.current();
						if (token) {
							requestConfig.headers.Authorization = `Bearer ${token}`;
						}

						const localStorageValue = loadFromLocalStorage(
							`${currentTenantLocalStorageKey}-${userContextRef.current.preferred_username}`
						);
						if (localStorageValue) {
							requestConfig.headers.RequestedTenant = localStorageValue;
						}

						return requestConfig;
					}
				);
			};

			configureInterceptor(apiInstance);
			interceptorsConfigured.current = true;
			setIsReady(true);
		}
	}, [initialized, isAuthenticated, apiInstance]);

	// Don't render children until interceptors are configured
	if (!isReady && !interceptorsConfigured.current) {
		return <div />;
	}

	return (
		<apiContext.Provider
			value={{
				apiInstance: apiInstance
			}}
		>
			{children}
		</apiContext.Provider>
	);
};

export default ApiProvider;
